import { computed, effect, inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  map,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import { environment } from '@env';

import { compareNewestFirst } from '@kernel';

import { AuthStore } from '@core/auth';
import { TOAST_DURATION, TOAST_PORT } from '@core/ports';

import { NotificationsService } from './notifications.service';
import { type NotificationsKind, type NotificationsState } from './notifications.types';

export const NotificationsStore = signalStore(
  { providedIn: 'root' },

  environment.withDevtools('notifications'),

  withState<NotificationsState>({ notifications: [], loading: false }),

  withComputed(({ notifications }) => {
    const newestFirst = computed(() =>
      [...notifications()].sort((left, right) => compareNewestFirst(left.created, right.created)),
    );
    const unreadItems = computed(() => newestFirst().filter((item) => !item.opened));
    const earlierItems = computed(() => newestFirst().filter((item) => item.opened));

    return {
      unreadItems,
      earlierItems,
      listedNotifications: computed(() => [...unreadItems(), ...earlierItems()]),
      unreadCount: computed(() => unreadItems().length),
    };
  }),

  withMethods((store) => {
    const toast = inject(TOAST_PORT);
    const authStore = inject(AuthStore);
    const notificationsService = inject(NotificationsService);

    const isCurrentContext = (contextKey: string | null): boolean =>
      authStore.authorizationContextKey() === contextKey;

    const resetNotifications = (): void => {
      patchState(store, { notifications: [], loading: false });
    };

    const notifyError = (message: string, extra: Partial<NotificationsState> = {}): void => {
      if (Object.keys(extra).length > 0) patchState(store, extra);

      toast.alert(
        { description: message, suffixIcon: 'close' },
        { duration: TOAST_DURATION.alert },
      );
    };

    const loadNotifications = rxMethod<NotificationsKind>(
      pipe(
        switchMap((kind) => {
          if (kind !== 'notifications') return EMPTY;

          const contextKey = authStore.authorizationContextKey();
          if (contextKey == null) {
            resetNotifications();
            return EMPTY;
          }

          patchState(store, { loading: true });

          return notificationsService.getNotifications().pipe(
            tapResponse({
              next: (notifications) => {
                if (!isCurrentContext(contextKey)) return;
                patchState(store, { notifications });
              },
              error: () => {
                if (!isCurrentContext(contextKey)) return;
                notifyError('header.notifications.loadError', { notifications: [] });
              },
            }),
            finalize(() => {
              if (isCurrentContext(contextKey)) patchState(store, { loading: false });
            }),
          );
        }),
      ),
    );

    const markNotificationsOpened = rxMethod<readonly number[]>(
      pipe(
        mergeMap((ids) => {
          if (!ids.length) return EMPTY;

          const contextKey = authStore.authorizationContextKey();
          if (contextKey == null) return EMPTY;

          const opened = new Set(ids);
          patchState(store, {
            notifications: store
              .notifications()
              .map((notification) =>
                opened.has(notification.id) ? { ...notification, opened: true } : notification,
              ),
          });

          return forkJoin(
            ids.map((id) =>
              notificationsService.openedNotification(id).pipe(
                map(() => true),
                catchError(() => of(false)),
              ),
            ),
          ).pipe(
            tap((results) => {
              if (!isCurrentContext(contextKey)) return;
              if (results.includes(false)) notifyError('header.notifications.markReadError');
            }),
          );
        }),
      ),
    );

    return { loadNotifications, markNotificationsOpened, resetNotifications };
  }),

  withHooks({
    onInit(store): void {
      const authStore = inject(AuthStore);
      let previousKey = authStore.authorizationContextKey();

      effect(() => {
        const contextKey = authStore.authorizationContextKey();
        if (contextKey === previousKey) return;

        previousKey = contextKey;
        store.resetNotifications();
      });
    },
  }),
);
